<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceLigandsTable.vue (toolbar, DataTable with
  removable sort and a frozen name column, row menu, footer buttons) and WorkspaceLigandsFilters.vue (the
  'n/total' filter button; only 'Filter by currently visible' is local). Columns are the ones the atoms give
  (Entry when there are several, Name as chain:residue_serial, Atoms, Formula, MW); prod's image, RDKit properties, ratings and tags need the server.
  Added for Lite: an eye per row for the matching 'Ligand ...' component, and row click selects and zooms.
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { saveAs } from 'file-saver';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import Popover from 'primevue/popover';
import ToggleSwitch from 'primevue/toggleswitch';
import type { MenuItem } from 'primevue/menuitem';
import { useLiteStore } from '@nanome-lite/state';
import { ligandComponent, ligandSdf, ligandSdfFilename, type LigandRow } from '@/ligands/ligands';
import EmptyResult from '../EmptyResult.vue';

const props = withDefaults(defineProps<{ ligands: LigandRow[]; isFullscreen?: boolean }>(), { isFullscreen: false });
const emit = defineEmits<{ fullscreen: []; sketch: [e: Event] }>();

const store = useLiteStore();
const search = ref('');
const visibleOnly = ref(false);
const selected = ref<LigandRow[]>([]);
const filterPopover = ref<InstanceType<typeof Popover>>();
const rowMenu = ref<InstanceType<typeof Menu>>();
const rowMenuLigand = ref<LigandRow | null>(null);

watch(() => props.ligands.map(l => l.id).join(), () => { selected.value = []; });

const componentOf = (row: LigandRow) => ligandComponent(row, store.components);
const isVisible = (row: LigandRow) => { const c = componentOf(row); return !!c && !c.hidden; };

const filteredLigands = computed(() => {
  const q = search.value.trim().toLowerCase();
  return props.ligands.filter(l =>
    (!q || [l.entry, l.name, l.label, l.chain, l.formula].some(v => v.toLowerCase().includes(q)))
    && (!visibleOnly.value || isVisible(l)));
});

/** The Entry column only earns its width when the ligands come from more than one entry. */
const multiEntry = computed(() => new Set(props.ligands.map(l => l.entrySerial)).size > 1);

const rows = computed(() => filteredLigands.value.map(l => ({ ...l, mw: l.weight })));

const rowMenuItems = computed<MenuItem[]>(() => {
  const ligand = rowMenuLigand.value;
  if (!ligand) return [];
  return [{
    label: 'Actions',
    items: [
      { label: 'Zoom to', icon: 'i-tabler-focus-centered', command: () => focus(ligand) },
      { label: 'Download SDF', icon: 'i-tabler-cloud-download', command: () => download(ligand) },
    ],
  }];
});

function eyeIcon(row: LigandRow) {
  const c = componentOf(row);
  if (!c) return 'i-tabler-eye-off';
  return c.hidden ? 'i-tabler-eye-off' : 'i-tabler-eye';
}

function toggleVisible(row: LigandRow) {
  const c = componentOf(row);
  if (c) store.toggleHidden([c.serial], !c.hidden);
}

/** Select the residue and frame its component (the entry when the component is gone). */
function focus(row: LigandRow) {
  store.selectResidues(row.entrySerial, [row.residueIndex], 'replace');
  const c = componentOf(row);
  if (c && !c.hidden) store.zoomComponent(c.serial);
  else store.zoomEntry(row.entrySerial);
}

function onRowClick(e: { originalEvent: Event; data: LigandRow }) {
  const target = e.originalEvent.target as HTMLElement | null;
  if (target?.closest('button, .p-checkbox')) return;
  focus(e.data);
}

function sdfOf(row: LigandRow): string | null {
  const model = store.models.get(row.entrySerial);
  return model ? ligandSdf(row, model) : null;
}

function download(row: LigandRow) {
  const sdf = sdfOf(row);
  if (sdf) saveAs(new Blob([sdf], { type: 'chemical/x-mdl-sdfile' }), ligandSdfFilename(row));
}

function downloadCombined(items: LigandRow[]) {
  const text = items.map(sdfOf).filter((s): s is string => !!s).join('');
  if (!text) return;
  const name = store.entries.length === 1 ? store.entries[0]!.name : 'ligands';
  saveAs(new Blob([text], { type: 'chemical/x-mdl-sdfile' }), `${name.replaceAll(':', '_')}_ligands.sdf`);
}

function openRowMenu(event: Event, data: LigandRow) {
  rowMenuLigand.value = data;
  rowMenu.value?.toggle(event);
}

const isRowSelected = (row: LigandRow) => {
  const atoms = store.selection.byEntry[row.entrySerial];
  return !!atoms?.length && atoms.includes(store.models.get(row.entrySerial)?.residues.atomStart[row.residueIndex] ?? -1);
};
</script>

<template>
  <div class="flex flex-wrap gap-2 items-center">
    <Button
      v-tooltip.top="'Filter ligands'"
      :label="`${filteredLigands.length}/${ligands.length}`"
      :outlined="!visibleOnly"
      icon="i-tabler-filter"
      severity="secondary"
      size="small"
      rounded
      data-test="ligands-count"
      @click="filterPopover?.toggle($event)"
    />
    <Popover ref="filterPopover" pt:content="w-22rem flex flex-col gap-3" class="rd-2">
      <div class="flex items-center justify-between gap-2">
        <label class="text-sm" for="ligands-visible-only">Filter by currently visible</label>
        <ToggleSwitch v-model="visibleOnly" input-id="ligands-visible-only" />
      </div>
    </Popover>
    <InputText v-model="search" class="ml-auto w-10rem" placeholder="Search ligands" size="small" data-test="ligands-search" />
    <Button
      v-if="!isFullscreen"
      v-tooltip.top="'Fullscreen'"
      icon="i-tabler-window-maximize"
      severity="secondary"
      size="small"
      rounded
      @click="emit('fullscreen')"
    />
  </div>

  <DataTable
    v-if="filteredLigands.length"
    v-model:selection="selected"
    :pt="{ thead: 'sticky p-0 z-2', tableContainer: 'min-h-0 grow-1' }"
    :value="rows"
    :row-class="(row: LigandRow) => (isRowSelected(row) ? 'ligand-row-selected' : '')"
    class="ligands-table min-h-0 -mx-4 grow-1 flex flex-col ws-nowrap"
    data-key="id"
    data-test="ligands-table"
    removable-sort
    scrollable
    @row-click="onRowClick"
  >
    <Column header="" header-class="px-0!" body-class="py-0 pl-2! pr-0!" frozen>
      <template #body="{ data }">
        <Button
          v-tooltip.top="componentOf(data) ? (componentOf(data)!.hidden ? 'Show' : 'Hide') : 'No component for this ligand'"
          :disabled="!componentOf(data)"
          :icon="eyeIcon(data)"
          severity="secondary"
          size="small"
          rounded
          text
          data-test="ligand-eye"
          @click="toggleVisible(data)"
        />
      </template>
    </Column>
    <Column v-if="multiEntry" field="entry" header="Entry" sortable />
    <Column field="label" header="Name" frozen sortable>
      <template #body="{ data }">
        <span :title="`Residue ${data.name} ${data.residueSerial}${data.insCode}, chain ${data.chain}`">{{ data.label }}</span>
      </template>
    </Column>
    <Column field="atomCount" header="Atoms" body-class="text-right" sortable />
    <Column field="formula" header="Formula" sortable />
    <Column field="mw" body-class="text-right" sortable>
      <template #header>
        <span class="font-semibold">MW</span>
        <i
          v-tooltip.top="'Molecular weight in g/mol of the atoms in the file. Hydrogens count only when the file has them.'"
          class="i-tabler-info-circle self-center op-50 hover:op-100"
        />
      </template>
      <template #body="{ data }">
        {{ data.mw.toFixed(2) }}
      </template>
    </Column>
    <Column header="" header-class="px-0!" body-class="py-0 px-1!">
      <template #body="{ data }">
        <Button
          v-tooltip.top="'Actions'"
          class="ml-auto"
          icon="i-tabler-dots-vertical"
          severity="secondary"
          size="small"
          text
          rounded
          @click="openRowMenu($event, data)"
        />
      </template>
    </Column>
    <Column selection-mode="multiple" header-style="width: 3rem" class="px-1.5" />
  </DataTable>

  <EmptyResult v-else item="matching ligands" icon="i-tabler-filter-x" class="min-h-0 grow-1" />

  <Menu ref="rowMenu" :model="rowMenuItems" popup />

  <div class="flex gap-2">
    <Button
      :disabled="!filteredLigands.length"
      :label="`Download ${selected.length || 'all'}`"
      icon="i-tabler-cloud-download"
      severity="secondary"
      size="small"
      rounded
      data-test="ligands-download"
      @click="downloadCombined(selected.length ? selected : filteredLigands)"
    />
    <Button
      label="Sketch new"
      icon="i-tabler-hexagon-plus-2"
      severity="secondary"
      size="small"
      rounded
      @click="emit('sketch', $event)"
    />
  </div>
</template>

<style>
/* the panel is 32rem wide: tighter cells than PrimeVue's 0.75rem 1rem so the row actions stay in view */
.ligands-table .p-datatable-thead > tr > th,
.ligands-table .p-datatable-tbody > tr > td {
  padding-inline: 0.625rem;
}
.ligands-table .p-datatable-tbody > tr {
  cursor: pointer;
}
.ligands-table .p-datatable-tbody > tr.ligand-row-selected > td {
  background: color-mix(in srgb, var(--primary-color), transparent 85%);
}
</style>
